import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  KeyRound,
  Mail,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserRound,
} from "lucide-react";
import { isEmailAddress } from "../../lib/email";
import { PageHeader } from "../../components/layout/PageHeader";
import { Modal } from "../../components/ui/Modal";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { useSession } from "../../hooks/useSession";
import {
  customersAdminApi,
  type ApiKey,
  type CustomerDetail,
  type InteractionPricing,
  type Login,
  type LoginGroup,
  type MiscCharge,
  type Option,
  type LoginMailKind,
  type TimeZoneOption,
  type CustomerSettingItem,
} from "../../api/customersAdminApi";

// Matches the app's shared `.field` input styling (see app.css) so the
// admin edit forms read the same as the customer-side detail screens.
const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "14px 16px",
  border: "2px solid var(--portal-border)",
  borderRadius: 8,
  fontSize: "1rem",
  fontFamily: "inherit",
  background: "white",
};

// Full-width list for the sub-lists (logins, API keys, misc charges,
// interaction pricing): one row per item spanning the section width,
// separated by a light divider — not cards.
const listStack: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
};
const listRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 2px",
  borderTop: "1px solid var(--portal-border)",
};
// Responsive field grid for edit forms (matches the customer-side detail
// screens that lay their fields out across the full width).
const fieldGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 14,
};

// smxSystems sentinel meaning "no end date".
const FAR_FUTURE = "2100-01-01";

function toDateInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function fromDateInput(value: string, fallback: string): string {
  const v = value || fallback;
  return `${v}T00:00:00Z`;
}

type LoadState =
  | { status: "loading" }
  | { status: "ready"; detail: CustomerDetail }
  | { status: "error"; message: string };

/**
 * Shared admin/manager customer editor. `/admin/customers/new` renders a
 * minimal create form (name + manager for admins); once created it
 * navigates to `/admin/customers/:customerId` which shows the full
 * detail: basics, billing/debtor, pincode, API keys, misc charges and
 * interaction pricing. Feature-flags are deliberately not exposed.
 */
export function CustomerEditPage() {
  const { customerId: param } = useParams<{ customerId: string }>();
  const isNew = param === "new";
  const customerId = Number(param);
  const navigate = useNavigate();
  const { session } = useSession();
  const isAdmin = session?.kind === "Admin";

  if (isNew) return <CustomerCreate isAdmin={isAdmin} />;

  return (
    <CustomerDetailEditor
      customerId={customerId}
      isAdmin={isAdmin}
      navigate={navigate}
    />
  );
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

function CustomerCreate({ isAdmin }: { isAdmin: boolean }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [managerId, setManagerId] = useState<number | "">("");
  const [managers, setManagers] = useState<Option[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    customersAdminApi
      .managerOptions()
      .then((rows) => active && setManagers(rows))
      .catch(() => {
        /* dropdown is best-effort */
      });
    return () => {
      active = false;
    };
  }, [isAdmin]);

  const submit = async () => {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (isAdmin && managerId === "") {
      setError("Select a manager.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const { customerId } = await customersAdminApi.create(
        name.trim(),
        isAdmin ? Number(managerId) : undefined,
      );
      navigate(`/admin/customers/${customerId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create customer");
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="New customer" actions={<BackToCustomers />} />
      <div className="dashboard">
        <section className="tile" style={{ maxWidth: 520, gap: 14 }}>
          {error && <ErrorLine message={error} />}
          <label style={{ display: "grid", gap: 4 }}>
            <FieldLabel>Name</FieldLabel>
            <input
              type="text"
              value={name}
              maxLength={50}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              className="portal-input-inline" style={inputStyle}
            />
          </label>
          {isAdmin && (
            <label style={{ display: "grid", gap: 4 }}>
              <FieldLabel>Manager</FieldLabel>
              <select
                value={managerId}
                onChange={(e) =>
                  setManagerId(e.target.value === "" ? "" : Number(e.target.value))
                }
                className="portal-input-inline" style={inputStyle}
              >
                <option value="">Select manager…</option>
                {managers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={submit}
              disabled={busy}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Plus size={16} />
              {busy ? "Creating…" : "Create customer"}
            </button>
          </div>
        </section>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Detail / edit
// ---------------------------------------------------------------------------

function CustomerDetailEditor({
  customerId,
  isAdmin,
  navigate,
}: {
  customerId: number;
  isAdmin: boolean;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  const refresh = useCallback(async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) {
      setState({ status: "error", message: "Invalid customer id." });
      return;
    }
    try {
      const detail = await customersAdminApi.getDetail(customerId);
      setState({ status: "ready", detail });
    } catch (err) {
      setState({
        status: "error",
        message: err instanceof Error ? err.message : "Failed to load customer",
      });
    }
  }, [customerId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <>
      <PageHeader
        title={
          state.status === "ready"
            ? state.detail.customerName || "Customer"
            : "Customer"
        }
        actions={<BackToCustomers />}
      />
      <div className="dashboard">
        {state.status === "loading" && (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading customer…
          </div>
        )}
        {state.status === "error" && (
          <div
            className="tile"
            style={{ borderColor: "#e74c3c", color: "#c0392b", background: "#fdecea" }}
          >
            <strong>Could not load this customer.</strong>
            <div style={{ marginTop: 6, fontSize: "0.9rem" }}>{state.message}</div>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 12 }}
              onClick={() => navigate("/admin/customers")}
            >
              Back to customers
            </button>
          </div>
        )}
        {state.status === "ready" && (
          <>
            <BasicsAndBilling
              detail={state.detail}
              isAdmin={isAdmin}
              onSaved={(d) => setState({ status: "ready", detail: d })}
            />
            {/* Logins + API keys stay visible for managers; misc charges
                and interaction pricing are billing config — admin-only. */}
            <LoginsSection customerId={customerId} />
            <ApiKeysSection customerId={customerId} />
            <CustomerSettingsSection customerId={customerId} isAdmin={isAdmin} />
            {isAdmin && (
              <>
                <TimeZoneSection customerId={customerId} />
                {state.detail.pbxEnabled && <PbxSettingsSection customerId={customerId} />}
                <MiscChargesSection customerId={customerId} />
                <InteractionPricingSection customerId={customerId} />
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

function BasicsAndBilling({
  detail,
  isAdmin,
  onSaved,
}: {
  detail: CustomerDetail;
  isAdmin: boolean;
  onSaved: (d: CustomerDetail) => void;
}) {
  const [name, setName] = useState(detail.customerName);
  const [enabled, setEnabled] = useState(detail.enabled);
  const [billable, setBillable] = useState(detail.billable);
  const [debtorId, setDebtorId] = useState<number | "">(detail.debtorId ?? "");
  const [managerId, setManagerId] = useState<number>(detail.managerId);
  const [debtors, setDebtors] = useState<Option[]>([]);
  const [managers, setManagers] = useState<Option[]>([]);
  const [pin, setPin] = useState<string>(String(detail.customerPin ?? ""));
  const [busy, setBusy] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    // Only admins see the billing block, so managers never need the
    // debtor / manager option lists.
    if (!isAdmin) return;
    let active = true;
    customersAdminApi
      .debtorOptions()
      .then((rows) => active && setDebtors(rows))
      .catch(() => undefined);
    customersAdminApi
      .managerOptions()
      .then((rows) => active && setManagers(rows))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [isAdmin]);

  const save = async () => {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setError(null);
    setBusy(true);
    setSaved(false);
    try {
      const updated = await customersAdminApi.update(detail.customerId, {
        customerName: name.trim(),
        enabled,
        billable,
        debtorId: debtorId === "" ? null : Number(debtorId),
        managerId: isAdmin ? managerId : undefined,
      });
      onSaved(updated);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save customer");
    } finally {
      setBusy(false);
    }
  };

  const generatePin = async () => {
    setPinBusy(true);
    try {
      const res = await customersAdminApi.generatePin(detail.customerId);
      if (res.pin) setPin(res.pin);
    } catch {
      /* surfaced via the disabled state; keep it quiet */
    } finally {
      setPinBusy(false);
    }
  };

  return (
    <section className="tile" style={{ gap: 16 }}>
      <header className="tile-head">
        <h3 style={{ margin: 0 }}>Details</h3>
      </header>
      {error && <ErrorLine message={error} />}

      <div style={fieldGrid}>
        <label style={{ display: "grid", gap: 6 }}>
          <FieldLabel>Name</FieldLabel>
          <input
            type="text"
            value={name}
            maxLength={150}
            onChange={(e) => setName(e.target.value)}
            className="portal-input-inline" style={inputStyle}
          />
        </label>

        {isAdmin && (
          <label style={{ display: "grid", gap: 6 }}>
            <FieldLabel>Manager</FieldLabel>
            <select
              value={managerId}
              onChange={(e) => setManagerId(Number(e.target.value))}
              className="portal-input-inline" style={inputStyle}
            >
              {managers.length === 0 && (
                <option value={managerId}>Manager #{managerId}</option>
              )}
              {managers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <span>Enabled</span>
      </label>

      {/* Pincode + billing are admin-only; managers manage their
          customers operationally, not their financial configuration. */}
      {isAdmin && (
        <>
          <div style={{ display: "grid", gap: 4 }}>
            <FieldLabel>Pincode</FieldLabel>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                style={{
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: "1.05rem",
                  minWidth: 70,
                }}
              >
                {pin || "—"}
              </span>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={generatePin}
                disabled={pinBusy}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}
              >
                <RefreshCw size={14} />
                {pinBusy ? "Generating…" : "Generate"}
              </button>
            </div>
          </div>

          <header className="tile-head" style={{ marginTop: 6 }}>
            <h4 style={{ margin: 0 }}>Billing</h4>
          </header>

          <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="checkbox"
              checked={billable}
              onChange={(e) => setBillable(e.target.checked)}
            />
            <span>Billable</span>
          </label>

          <label style={{ display: "grid", gap: 4 }}>
            <FieldLabel>Debtor</FieldLabel>
            <select
              value={debtorId}
              onChange={(e) =>
                setDebtorId(e.target.value === "" ? "" : Number(e.target.value))
              }
              className="portal-input-inline" style={inputStyle}
            >
              <option value="">No debtor</option>
              {debtors.length === 0 && detail.debtorId != null && (
                <option value={detail.debtorId}>{detail.debtorName || `Debtor #${detail.debtorId}`}</option>
              )}
              {debtors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        </>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "flex-end" }}>
        {saved && (
          <span style={{ color: "var(--portal-status-success-darker)", fontSize: "0.85rem" }}>
            Saved
          </span>
        )}
        <button
          type="button"
          className="btn btn-primary"
          onClick={save}
          disabled={busy}
        >
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Logins (+ per-login permission groups)
// ---------------------------------------------------------------------------

function LoginsSection({ customerId }: { customerId: number }) {
  const [rows, setRows] = useState<Login[] | null>(null);
  const [editing, setEditing] = useState<Login | "new" | null>(null);
  const [groupsFor, setGroupsFor] = useState<Login | null>(null);
  const [removePending, setRemovePending] = useState<Login | null>(null);
  const [mailFor, setMailFor] = useState<Login | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  // Auto-hide the "Sent to …" line.
  useEffect(() => {
    if (!notice?.ok) return;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  useEffect(() => {
    let active = true;
    customersAdminApi
      .logins(customerId)
      .then((r) => active && setRows(r))
      .catch(() => active && setRows([]));
    return () => {
      active = false;
    };
  }, [customerId]);

  const remove = async (loginId: number) => {
    setBusy(true);
    try {
      setRows(await customersAdminApi.deleteLogin(customerId, loginId));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="tile" style={{ gap: 12 }}>
      <header className="tile-head" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h3 style={{ margin: 0 }}>Logins</h3>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setEditing("new")}
          style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}
        >
          <Plus size={14} /> Add login
        </button>
      </header>
      {rows == null ? (
        <div style={{ color: "var(--portal-muted)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: "var(--portal-muted)" }}>No logins.</div>
      ) : (
        <div style={listStack}>
          {rows.map((l) => (
            <div key={l.loginId} style={listRow}>
              <UserRound size={16} style={{ color: "var(--portal-muted)", flexShrink: 0 }} />
              <span
                style={{ flex: 1, minWidth: 0, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                title={l.username}
              >
                {l.username || "—"}
              </span>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setMailFor(l)}
                disabled={!isEmailAddress(l.username)}
                title={isEmailAddress(l.username) ? "Send an e-mail to this login" : "The username is not an e-mail address"}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem", flexShrink: 0 }}
              >
                <Mail size={14} /> Mail
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setGroupsFor(l)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem", flexShrink: 0 }}
              >
                <ShieldCheck size={14} /> Permissions
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setEditing(l)}
                title="Edit login"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem", flexShrink: 0 }}
              >
                <Pencil size={14} /> Edit
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setRemovePending(l)}
                title="Delete login"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem", flexShrink: 0 }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {notice && (
        <div style={{ fontSize: "0.85rem", color: notice.ok ? "var(--portal-status-success-darker)" : "#c0392b" }}>
          {notice.text}
        </div>
      )}

      {editing && (
        <LoginModal
          customerId={customerId}
          value={editing === "new" ? null : editing}
          existing={rows ?? []}
          onClose={() => setEditing(null)}
          onSaved={(next, sent) => {
            setRows(next);
            setEditing(null);
            if (sent) setNotice(sent);
          }}
        />
      )}

      {mailFor && (
        <LoginMailModal
          customerId={customerId}
          login={mailFor}
          onClose={() => setMailFor(null)}
          onSent={(text) => {
            setMailFor(null);
            setNotice({ ok: true, text });
          }}
        />
      )}

      {groupsFor && (
        <LoginGroupsModal
          customerId={customerId}
          login={groupsFor}
          onClose={() => setGroupsFor(null)}
        />
      )}

      <ConfirmModal
        open={removePending != null}
        onClose={() => {
          if (!busy) setRemovePending(null);
        }}
        title="Delete login?"
        confirmLabel="Delete"
        tone="danger"
        busy={busy}
        onConfirm={() => {
          const l = removePending;
          setRemovePending(null);
          if (l) void remove(l.loginId);
        }}
      >
        Delete login <strong>{removePending?.username}</strong>? This cannot be undone.
      </ConfirmModal>
    </section>
  );
}

function LoginModal({
  customerId,
  value,
  existing,
  onClose,
  onSaved,
}: {
  customerId: number;
  value: Login | null;
  /** Current logins, used to find the id of a newly created login. */
  existing: Login[];
  onClose: () => void;
  onSaved: (rows: Login[], sent?: { ok: boolean; text: string }) => void;
}) {
  const isNew = value == null;
  const [username, setUsername] = useState(value?.username ?? "");
  const [password, setPassword] = useState("");
  const [sendWelcome, setSendWelcome] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mailable = isEmailAddress(username);

  const save = async () => {
    if (!username.trim()) {
      setError("Username is required.");
      return;
    }
    if (isNew && !password) {
      setError("A password is required for a new login.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const rows = await customersAdminApi.upsertLogin(customerId, {
        loginId: value?.loginId ?? 0,
        username: username.trim(),
        password,
      });
      // Welcome mail for a brand-new login: its id is the one that wasn't there before.
      if (isNew && sendWelcome && mailable) {
        const known = new Set(existing.map((l) => l.loginId));
        const created = rows.find((l) => !known.has(l.loginId));
        if (created) {
          try {
            const r = await customersAdminApi.sendLoginMail(customerId, created.loginId, "welcome");
            onSaved(rows, { ok: true, text: `Login created; welcome e-mail sent to ${r.sentTo}.` });
          } catch (err) {
            onSaved(rows, { ok: false, text: `Login created, but the welcome e-mail failed: ${mailErrorText(err)}` });
          }
          return;
        }
      }
      onSaved(rows);
    } catch (err) {
      const m = err instanceof Error ? err.message : "";
      setError(
        m === "username_taken"
          ? "This username is already used by another login (a customer, agent or user login). Choose a different username."
          : m || "Failed to save login",
      );
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      title={isNew ? "Add login" : "Edit login"}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        {error && <ErrorLine message={error} />}
        <label style={{ display: "grid", gap: 4 }}>
          <FieldLabel>Username</FieldLabel>
          <input
            type="text"
            value={username}
            maxLength={50}
            autoComplete="off"
            onChange={(e) => setUsername(e.target.value)}
            className="portal-input-inline" style={inputStyle}
          />
        </label>
        <label style={{ display: "grid", gap: 4 }}>
          <FieldLabel>{isNew ? "Password" : "New password (leave blank to keep current)"}</FieldLabel>
          <input
            type="text"
            value={password}
            maxLength={50}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
            className="portal-input-inline" style={inputStyle}
          />
        </label>
        {isNew && (
          <label style={{ display: "flex", alignItems: "center", gap: 10, opacity: mailable ? 1 : 0.6 }}>
            <input type="checkbox" checked={sendWelcome && mailable} disabled={!mailable} onChange={(e) => setSendWelcome(e.target.checked)} />
            <span>
              Send welcome e-mail after creating
              {!mailable && <span style={{ color: "var(--portal-muted)" }}> (username must be an e-mail address)</span>}
            </span>
          </label>
        )}
      </div>
    </Modal>
  );
}

const MAIL_ERRORS: Record<string, string> = {
  username_not_email: "the username is not an e-mail address",
  mail_not_configured: "no sender address is configured for this manager",
  login_not_found: "the login was not found",
};
function mailErrorText(err: unknown): string {
  const m = err instanceof Error ? err.message : "";
  return MAIL_ERRORS[m] ?? (m || "unknown error");
}

const MAIL_KINDS: { kind: LoginMailKind; label: string; description: string }[] = [
  { kind: "welcome", label: "Welcome e-mail", description: "Account created, with a link to set a password (valid 72 hours)." },
  { kind: "username", label: "Username reminder", description: "The username and a link to the sign-in page." },
  { kind: "reset", label: "Password reset link", description: "A link to choose a new password (valid 24 hours)." },
];

/** Choose which e-mail to send to a login; the mail is queued by the API in the manager's brand style. */
function LoginMailModal({
  customerId,
  login,
  onClose,
  onSent,
}: {
  customerId: number;
  login: Login;
  onClose: () => void;
  onSent: (text: string) => void;
}) {
  const [kind, setKind] = useState<LoginMailKind>("reset");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await customersAdminApi.sendLoginMail(customerId, login.loginId, kind);
      onSent(`${MAIL_KINDS.find((k) => k.kind === kind)?.label ?? "E-mail"} sent to ${r.sentTo}.`);
    } catch (err) {
      setError(`Could not send: ${mailErrorText(err)}.`);
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      title={`E-mail ${login.username}`}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void send()} disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Mail size={14} /> {busy ? "Sending…" : "Send"}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        {error && <ErrorLine message={error} />}
        {MAIL_KINDS.map((k) => (
          <label key={k.kind} style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
            <input type="radio" name="login-mail-kind" checked={kind === k.kind} onChange={() => setKind(k.kind)} style={{ marginTop: 3 }} />
            <span>
              <span style={{ fontWeight: 600 }}>{k.label}</span>
              <br />
              <span style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>{k.description}</span>
            </span>
          </label>
        ))}
        <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--portal-muted)" }}>
          The e-mail is sent in the brand style of this customer's manager. A new link replaces any earlier one.
        </p>
      </div>
    </Modal>
  );
}

function LoginGroupsModal({
  customerId,
  login,
  onClose,
}: {
  customerId: number;
  login: Login;
  onClose: () => void;
}) {
  const [groups, setGroups] = useState<LoginGroup[] | null>(null);
  const [pending, setPending] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    customersAdminApi
      .loginGroups(customerId, login.loginId)
      .then((r) => active && setGroups(r))
      .catch(() => active && setGroups([]));
    return () => {
      active = false;
    };
  }, [customerId, login.loginId]);

  const toggle = async (groupId: number, assigned: boolean) => {
    setPending(groupId);
    try {
      const next = assigned
        ? await customersAdminApi.removeLoginGroup(customerId, login.loginId, groupId)
        : await customersAdminApi.addLoginGroup(customerId, login.loginId, groupId);
      setGroups(next);
    } finally {
      setPending(null);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Permission groups · ${login.username}`}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <div style={{ display: "grid", gap: 6 }}>
        <p style={{ margin: "0 0 4px 0", fontSize: "0.85rem", color: "var(--portal-muted)" }}>
          Changes take effect the next time this login signs in.
        </p>
        {groups == null ? (
          <div style={{ color: "var(--portal-muted)" }}>Loading…</div>
        ) : groups.length === 0 ? (
          <div style={{ color: "var(--portal-muted)" }}>No permission groups defined.</div>
        ) : (
          groups.map((g) => (
            <label
              key={g.portalPermissionGroupId}
              style={{ display: "flex", alignItems: "center", gap: 10, opacity: pending === g.portalPermissionGroupId ? 0.5 : 1 }}
            >
              <input
                type="checkbox"
                checked={g.assigned}
                disabled={pending != null}
                onChange={() => void toggle(g.portalPermissionGroupId, g.assigned)}
              />
              <span>{g.name}</span>
            </label>
          ))
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// API keys
// ---------------------------------------------------------------------------

function ApiKeysSection({ customerId }: { customerId: number }) {
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [removePending, setRemovePending] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    customersAdminApi
      .apiKeys(customerId)
      .then((rows) => active && setKeys(rows))
      .catch(() => active && setKeys([]));
    return () => {
      active = false;
    };
  }, [customerId]);

  const create = async () => {
    setBusy(true);
    try {
      setKeys(await customersAdminApi.createApiKey(customerId));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (apiKeyCustomerId: number) => {
    setBusy(true);
    try {
      setKeys(await customersAdminApi.deleteApiKey(customerId, apiKeyCustomerId));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="tile" style={{ gap: 12 }}>
      <header className="tile-head" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h3 style={{ margin: 0 }}>API keys</h3>
        <button
          type="button"
          className="btn btn-primary"
          onClick={create}
          disabled={busy}
          style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}
        >
          <KeyRound size={14} /> New key
        </button>
      </header>
      {keys == null ? (
        <div style={{ color: "var(--portal-muted)" }}>Loading…</div>
      ) : keys.length === 0 ? (
        <div style={{ color: "var(--portal-muted)" }}>No API keys.</div>
      ) : (
        <div style={listStack}>
          {keys.map((k) => (
            <div key={k.apiKeyCustomerId} style={listRow}>
              <code
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "0.85rem",
                }}
                title={k.apiKey}
              >
                {k.apiKey}
              </code>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setRemovePending(k.apiKeyCustomerId)}
                disabled={busy}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem", flexShrink: 0 }}
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={removePending != null}
        onClose={() => {
          if (!busy) setRemovePending(null);
        }}
        title="Delete API key?"
        confirmLabel="Delete"
        tone="caution"
        busy={busy}
        onConfirm={() => {
          const id = removePending;
          setRemovePending(null);
          if (id != null) void remove(id);
        }}
      >
        This API key will stop working immediately.
      </ConfirmModal>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Customer settings (dbo.CustomerSetting, typed by dbo.CustomerSettingDefinition, script 25)
// ---------------------------------------------------------------------------

const SETTING_ERRORS: Record<string, string> = {
  value_too_long: "Value is too long (max 500 characters).",
  value_not_boolean: "Value must be on or off.",
  value_not_integer: "Value must be a whole number.",
  value_not_number: "Value must be a number.",
  value_below_minimum: "Value is below the minimum.",
  value_above_maximum: "Value is above the maximum.",
  value_not_email: "Value must be an e-mail address.",
  value_not_an_option: "Value is not one of the options.",
  setting_unknown: "Unknown setting.",
  definitions_unavailable: "Run api/Scripts/25-customer-setting-definitions.sql first.",
};

const settingErrorText = (err: unknown, fallback: string) => {
  const msg = err instanceof Error ? err.message : "";
  return SETTING_ERRORS[msg] ?? (msg || fallback);
};

/** Human form of a stored value, for the "default" hint. */
function settingValueLabel(item: CustomerSettingItem, value: string | null): string {
  if (value === null || value === "") return "empty";
  if (item.valueType === "bool") return value === "1" || value.toLowerCase() === "true" ? "on" : "off";
  if (item.valueType === "select") return item.options.find((o) => o.value === value)?.label ?? value;
  return value;
}

/**
 * Every customer setting with a control that fits its type. Admins see all settings and tick
 * per setting whether managers may edit it; managers see only the ticked ones. Bool and select
 * save on change, the other types on Save/Enter. "Reset" removes the customer's override so
 * the global default applies again.
 */
function CustomerSettingsSection({ customerId, isAdmin }: { customerId: number; isAdmin: boolean }) {
  const [items, setItems] = useState<CustomerSettingItem[] | null>(null);
  const [definitionsAvailable, setDefinitionsAvailable] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    customersAdminApi
      .listSettings(customerId)
      .then((r) => {
        if (!active) return;
        setItems(r.items);
        setDefinitionsAvailable(r.definitionsAvailable);
      })
      .catch((err) => active && setError(err instanceof Error ? err.message : "Failed to load settings"));
    return () => {
      active = false;
    };
  }, [customerId]);

  const replace = (next: CustomerSettingItem) =>
    setItems((cur) => (cur ? cur.map((i) => (i.settingName === next.settingName ? next : i)) : cur));

  if (error) {
    return (
      <section className="tile" style={{ display: "grid", gap: 12 }}>
        <header className="tile-head"><h3 style={{ margin: 0 }}>Customer settings</h3></header>
        <ErrorLine message={error} />
      </section>
    );
  }
  if (!items) return null;
  // A manager without any editable setting gets no empty tile.
  if (!isAdmin && items.length === 0) return null;

  const categories: string[] = [];
  for (const i of items) if (!categories.includes(i.category)) categories.push(i.category);

  return (
    <section className="tile" style={{ display: "grid", gap: 14 }}>
      <header className="tile-head">
        <h3 style={{ margin: 0 }}>Customer settings</h3>
      </header>
      <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
        {isAdmin
          ? "A value set here overrides the global default for this customer. Tick \"Manager\" to let the customer's manager change that setting."
          : "A value set here overrides the default for this customer."}
      </p>
      {isAdmin && !definitionsAvailable && (
        <div style={{ fontSize: "0.85rem", color: "#8a6d3b", background: "#fff8e6", border: "1px solid #f0d58c", borderRadius: 8, padding: "8px 10px" }}>
          Settings are shown as free text until api/Scripts/25-customer-setting-definitions.sql has been run.
        </div>
      )}
      {items.length === 0 && <div style={{ color: "var(--portal-muted)", fontSize: "0.9rem" }}>No settings.</div>}
      {categories.map((category) => (
        <div key={category} style={{ display: "grid", gap: 8 }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--portal-muted)" }}>{category}</div>
          <div style={{ display: "grid", gap: 6 }}>
            {items.filter((i) => i.category === category).map((item) => (
              <CustomerSettingRow key={item.settingName} customerId={customerId} item={item} isAdmin={isAdmin} onChanged={replace} />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function CustomerSettingRow({
  customerId,
  item,
  isAdmin,
  onChanged,
}: {
  customerId: number;
  item: CustomerSettingItem;
  isAdmin: boolean;
  onChanged: (next: CustomerSettingItem) => void;
}) {
  const [draft, setDraft] = useState(item.value ?? "");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [flagBusy, setFlagBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(item.value ?? "");
  }, [item.value, item.isCustomerSpecific]);

  const isOn = (v: string | null) => v === "1" || (v ?? "").toLowerCase() === "true";
  const dirty = draft !== (item.value ?? "");

  const save = async (value: string) => {
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      onChanged(await customersAdminApi.setSetting(customerId, item.settingName, value));
      setSaved(true);
    } catch (err) {
      setError(settingErrorText(err, "Failed to save"));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      onChanged(await customersAdminApi.resetSetting(customerId, item.settingName));
      setSaved(true);
    } catch (err) {
      setError(settingErrorText(err, "Failed to reset"));
    } finally {
      setBusy(false);
    }
  };

  const flag = async (managerEditable: boolean) => {
    setFlagBusy(true);
    setError(null);
    try {
      await customersAdminApi.setSettingManagerEditable(item.settingName, managerEditable);
      onChanged({ ...item, managerEditable, defined: true });
    } catch (err) {
      setError(settingErrorText(err, "Failed to update the manager flag"));
    } finally {
      setFlagBusy(false);
    }
  };

  const numberStep = item.valueType === "decimal" ? "0.01" : "1";
  const control = (() => {
    switch (item.valueType) {
      case "bool":
        return (
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: busy ? "default" : "pointer" }}>
            <input type="checkbox" checked={isOn(item.value)} disabled={busy} onChange={(e) => void save(e.target.checked ? "1" : "0")} />
            <span>{isOn(item.value) ? "On" : "Off"}</span>
          </label>
        );
      case "select":
        return (
          <select className="portal-input-inline" style={{ ...inputStyle, maxWidth: 260 }} value={item.value ?? ""} disabled={busy} onChange={(e) => void save(e.target.value)}>
            {(item.value === null || item.value === "" || !item.options.some((o) => o.value === item.value)) && (
              <option value={item.value ?? ""}>{item.value ? `${item.value} (not an option)` : "— choose —"}</option>
            )}
            {item.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        );
      default: {
        const type = item.valueType === "int" || item.valueType === "decimal" ? "number" : item.valueType === "email" ? "email" : "text";
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              className="portal-input-inline"
              style={{ ...inputStyle, maxWidth: 260 }}
              type={type}
              step={type === "number" ? numberStep : undefined}
              min={type === "number" && item.minValue !== null ? item.minValue : undefined}
              max={type === "number" && item.maxValue !== null ? item.maxValue : undefined}
              value={draft}
              disabled={busy}
              onChange={(e) => { setDraft(e.target.value); setSaved(false); }}
              onKeyDown={(e) => { if (e.key === "Enter" && dirty) void save(draft); }}
            />
            {dirty && (
              <button type="button" className="btn btn-primary" style={{ padding: "4px 10px" }} disabled={busy} onClick={() => void save(draft)}>Save</button>
            )}
          </span>
        );
      }
    }
  })();

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isAdmin ? "minmax(220px, 1.2fr) minmax(220px, 1fr) auto" : "minmax(220px, 1.2fr) minmax(220px, 1fr)",
        gap: "4px 16px",
        alignItems: "center",
        padding: "8px 10px",
        borderRadius: 8,
        background: item.isCustomerSpecific ? "#fff8e6" : "transparent",
        border: "1px solid var(--portal-border)",
      }}
    >
      <div style={{ display: "grid", gap: 2 }}>
        <span style={{ fontWeight: 600 }} title={item.settingName}>{item.displayName}</span>
        {item.description && <span style={{ fontSize: "0.8rem", color: "var(--portal-muted)" }}>{item.description}</span>}
        {!item.defined && isAdmin && <span style={{ fontSize: "0.75rem", color: "var(--portal-muted)" }}>No definition yet: edited as text.</span>}
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        {control}
        <span style={{ fontSize: "0.78rem", color: "var(--portal-muted)" }}>
          {item.isCustomerSpecific ? (
            <>
              Customer-specific · default: {settingValueLabel(item, item.defaultValue)} ·{" "}
              <button type="button" style={{ padding: 0, border: 0, background: "none", color: "var(--portal-link, #1a73e8)", cursor: "pointer", fontSize: "inherit", textDecoration: "underline" }} disabled={busy} onClick={() => void reset()}>Reset to default</button>
            </>
          ) : (
            <>Default: {settingValueLabel(item, item.defaultValue)}</>
          )}
          {busy ? " · saving…" : saved ? " · saved" : ""}
        </span>
        {error && <span style={{ fontSize: "0.8rem", color: "#c0392b" }}>{error}</span>}
      </div>
      {isAdmin && (
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.85rem", whiteSpace: "nowrap" }} title="Managers may change this setting for their customers">
          <input type="checkbox" checked={item.managerEditable} disabled={flagBusy} onChange={(e) => void flag(e.target.checked)} />
          Manager
        </label>
      )}
    </div>
  );
}

// Report time zone (CustomerSetting 'TimeZone')

/**
 * The customer's report time zone: every report (Inbound/Outbound, CDR,
 * Recordings, live calls) shows and filters times in this zone; the
 * database keeps UTC. Empty = UTC. Applies to new sessions of the customer.
 */
function TimeZoneSection({ customerId }: { customerId: number }) {
  const [options, setOptions] = useState<TimeZoneOption[]>([]);
  const [value, setValue] = useState<string>("");
  const [iana, setIana] = useState<string>("UTC");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    customersAdminApi.timezoneOptions().then((o) => active && setOptions(o)).catch(() => undefined);
    customersAdminApi
      .getTimeZone(customerId)
      .then((t) => {
        if (!active) return;
        setValue(t.timeZone ?? "");
        setIana(t.timeZoneIana);
      })
      .catch((err) => active && setError(err instanceof Error ? err.message : "Failed to load time zone"));
    return () => {
      active = false;
    };
  }, [customerId]);

  const change = async (next: string) => {
    setValue(next);
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      const t = await customersAdminApi.setTimeZone(customerId, next || null);
      setValue(t.timeZone ?? "");
      setIana(t.timeZoneIana);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save time zone");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="tile" style={{ display: "grid", gap: 12 }}>
      <header className="tile-head">
        <h3 style={{ margin: 0 }}>Report time zone</h3>
      </header>
      <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
        Reports show and filter times in this zone (the database keeps UTC). Takes effect on the customer's next sign-in.
      </p>
      <label style={{ display: "grid", gap: 6, maxWidth: 480 }}>
        <FieldLabel>Time zone</FieldLabel>
        <select className="portal-input-inline" style={inputStyle} value={value} onChange={(e) => void change(e.target.value)} disabled={busy}>
          <option value="">UTC (default)</option>
          {value && !options.some((o) => o.id === value) && <option value={value}>{value} (not installed)</option>}
          {options.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
      </label>
      <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
        Shown in reports as <strong style={{ color: "inherit" }}>{iana}</strong>
        {busy ? " · saving…" : saved ? " · saved" : ""}
      </div>
      {error && <ErrorLine message={error} />}
    </section>
  );
}

// IPPBX settings (CustomerSetting 'PbxInternalCalls', script 20)
// ---------------------------------------------------------------------------

function PbxSettingsSection({ customerId }: { customerId: number }) {
  const [internalCalls, setInternalCalls] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    customersAdminApi
      .getPbxSettings(customerId)
      .then((s) => {
        if (!active) return;
        setInternalCalls(s.internalCalls);
        setLoaded(true);
      })
      .catch((err) => active && setError(err instanceof Error ? err.message : "Failed to load PBX settings"));
    return () => {
      active = false;
    };
  }, [customerId]);

  const change = async (next: boolean) => {
    setInternalCalls(next);
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      const s = await customersAdminApi.setPbxSettings(customerId, { internalCalls: next });
      setInternalCalls(s.internalCalls);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save PBX settings");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="tile" style={{ display: "grid", gap: 12 }}>
      <header className="tile-head">
        <h3 style={{ margin: 0 }}>IPPBX</h3>
      </header>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
        <input type="checkbox" checked={internalCalls} disabled={!loaded || busy} onChange={(e) => void change(e.target.checked)} />
        <span>Allow internal calls and transfers between the customer's extensions</span>
      </label>
      <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
        When on, a phone registered on the PBX can dial the 4-digit extension number of a colleague (set on the destination page) and
        transfer a caller to a colleague with the phone's transfer key. A colleague who is busy or unavailable gives a busy signal on an
        internal call; a transferred caller is offered the colleague's voicemail when that is enabled.
        {busy ? " · saving…" : saved ? " · saved" : ""}
      </p>
      {error && <ErrorLine message={error} />}
    </section>
  );
}

// Misc charges (NRC/MRC)
// ---------------------------------------------------------------------------

function MiscChargesSection({ customerId }: { customerId: number }) {
  const [rows, setRows] = useState<MiscCharge[] | null>(null);
  const [editing, setEditing] = useState<MiscCharge | "new" | null>(null);
  const [busy, setBusy] = useState(false);
  const [removePending, setRemovePending] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    customersAdminApi
      .miscCharges(customerId)
      .then((r) => active && setRows(r))
      .catch(() => active && setRows([]));
    return () => {
      active = false;
    };
  }, [customerId]);

  const remove = async (id: number) => {
    setBusy(true);
    try {
      setRows(await customersAdminApi.deleteMiscCharge(customerId, id));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="tile" style={{ gap: 12 }}>
      <header className="tile-head" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h3 style={{ margin: 0 }}>Misc charges</h3>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setEditing("new")}
          style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}
        >
          <Plus size={14} /> Add charge
        </button>
      </header>
      {rows == null ? (
        <div style={{ color: "var(--portal-muted)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: "var(--portal-muted)" }}>No misc charges.</div>
      ) : (
        <div style={listStack}>
          {rows.map((r) => (
            <div key={r.customerMiscChargesId} style={listRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.description || "—"}
                </div>
                <div style={{ color: "var(--portal-muted)", fontSize: "0.8rem" }}>
                  NRC {r.nrc.toFixed(2)} · MRC {r.mrc.toFixed(2)}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setEditing(r)}
                style={{ fontSize: "0.8rem", flexShrink: 0 }}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setRemovePending(r.customerMiscChargesId)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem", flexShrink: 0 }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <MiscChargeModal
          customerId={customerId}
          value={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(next) => {
            setRows(next);
            setEditing(null);
          }}
        />
      )}

      <ConfirmModal
        open={removePending != null}
        onClose={() => {
          if (!busy) setRemovePending(null);
        }}
        title="Delete misc charge?"
        confirmLabel="Delete"
        tone="caution"
        busy={busy}
        onConfirm={() => {
          const id = removePending;
          setRemovePending(null);
          if (id != null) void remove(id);
        }}
      >
        This charge will be removed.
      </ConfirmModal>
    </section>
  );
}

function MiscChargeModal({
  customerId,
  value,
  onClose,
  onSaved,
}: {
  customerId: number;
  value: MiscCharge | null;
  onClose: () => void;
  onSaved: (rows: MiscCharge[]) => void;
}) {
  const [description, setDescription] = useState(value?.description ?? "");
  const [nrc, setNrc] = useState(String(value?.nrc ?? "0"));
  const [mrc, setMrc] = useState(String(value?.mrc ?? "0"));
  const [validFrom, setValidFrom] = useState(toDateInput(value?.validFromUtc) || toDateInput(new Date().toISOString()));
  const [validTill, setValidTill] = useState(toDateInput(value?.validTillUtc) || FAR_FUTURE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const nrcNum = Number(nrc);
    const mrcNum = Number(mrc);
    if (Number.isNaN(nrcNum) || Number.isNaN(mrcNum)) {
      setError("NRC and MRC must be numbers.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const rows = await customersAdminApi.upsertMiscCharge(customerId, {
        customerMiscChargesId: value?.customerMiscChargesId ?? 0,
        description: description.trim(),
        nrc: nrcNum,
        mrc: mrcNum,
        validFromUtc: fromDateInput(validFrom, toDateInput(new Date().toISOString())),
        validTillUtc: fromDateInput(validTill, FAR_FUTURE),
      });
      onSaved(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save charge");
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      title={value ? "Edit misc charge" : "Add misc charge"}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        {error && <ErrorLine message={error} />}
        <label style={{ display: "grid", gap: 4 }}>
          <FieldLabel>Description</FieldLabel>
          <input type="text" value={description} maxLength={150} onChange={(e) => setDescription(e.target.value)} className="portal-input-inline" style={inputStyle} />
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label style={{ display: "grid", gap: 4 }}>
            <FieldLabel>NRC (one-off)</FieldLabel>
            <input type="number" step="0.01" value={nrc} onChange={(e) => setNrc(e.target.value)} className="portal-input-inline" style={inputStyle} />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <FieldLabel>MRC (monthly)</FieldLabel>
            <input type="number" step="0.01" value={mrc} onChange={(e) => setMrc(e.target.value)} className="portal-input-inline" style={inputStyle} />
          </label>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label style={{ display: "grid", gap: 4 }}>
            <FieldLabel>Valid from</FieldLabel>
            <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="portal-input-inline" style={inputStyle} />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <FieldLabel>Valid till</FieldLabel>
            <input type="date" value={validTill} onChange={(e) => setValidTill(e.target.value)} className="portal-input-inline" style={inputStyle} />
          </label>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Interaction pricing
// ---------------------------------------------------------------------------

function InteractionPricingSection({ customerId }: { customerId: number }) {
  const [rows, setRows] = useState<InteractionPricing[] | null>(null);
  const [editing, setEditing] = useState<InteractionPricing | "new" | null>(null);

  useEffect(() => {
    let active = true;
    customersAdminApi
      .interactionPricing(customerId)
      .then((r) => active && setRows(r))
      .catch(() => active && setRows([]));
    return () => {
      active = false;
    };
  }, [customerId]);

  return (
    <section className="tile" style={{ gap: 12 }}>
      <header className="tile-head" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h3 style={{ margin: 0 }}>Interaction pricing</h3>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setEditing("new")}
          style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}
        >
          <Plus size={14} /> Add pricing
        </button>
      </header>
      {rows == null ? (
        <div style={{ color: "var(--portal-muted)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: "var(--portal-muted)" }}>No interaction pricing.</div>
      ) : (
        <div style={listStack}>
          {rows.map((r) => (
            <div key={r.interactionPricingId} style={listRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{r.siteName || (r.siteId ? `Site #${r.siteId}` : "All sites")}</div>
                <div style={{ color: "var(--portal-muted)", fontSize: "0.8rem" }}>
                  {r.perSession.toFixed(4)} / session · {r.perMinute.toFixed(4)} / min
                  {r.isActive ? "" : " · inactive"}
                </div>
              </div>
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(r)} style={{ fontSize: "0.8rem", flexShrink: 0 }}>
                Edit
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <InteractionPricingModal
          customerId={customerId}
          value={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(next) => {
            setRows(next);
            setEditing(null);
          }}
        />
      )}
    </section>
  );
}

function InteractionPricingModal({
  customerId,
  value,
  onClose,
  onSaved,
}: {
  customerId: number;
  value: InteractionPricing | null;
  onClose: () => void;
  onSaved: (rows: InteractionPricing[]) => void;
}) {
  const [siteId, setSiteId] = useState(String(value?.siteId ?? "0"));
  const [perSession, setPerSession] = useState(String(value?.perSession ?? "0"));
  const [perMinute, setPerMinute] = useState(String(value?.perMinute ?? "0"));
  const [validFrom, setValidFrom] = useState(toDateInput(value?.validFromUtc) || toDateInput(new Date().toISOString()));
  const [validTill, setValidTill] = useState(toDateInput(value?.validTillUtc) || FAR_FUTURE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const s = Number(perSession);
    const m = Number(perMinute);
    const site = Number(siteId);
    if (Number.isNaN(s) || Number.isNaN(m) || Number.isNaN(site)) {
      setError("Site, per-session and per-minute must be numbers.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const rows = await customersAdminApi.upsertInteractionPricing(customerId, {
        interactionPricingId: value?.interactionPricingId ?? 0,
        siteId: site,
        perSession: s,
        perMinute: m,
        validFromUtc: fromDateInput(validFrom, toDateInput(new Date().toISOString())),
        validTillUtc: fromDateInput(validTill, FAR_FUTURE),
      });
      onSaved(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save pricing");
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      title={value ? "Edit interaction pricing" : "Add interaction pricing"}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        {error && <ErrorLine message={error} />}
        <label style={{ display: "grid", gap: 4 }}>
          <FieldLabel>Site id (0 = all sites)</FieldLabel>
          <input type="number" value={siteId} onChange={(e) => setSiteId(e.target.value)} className="portal-input-inline" style={inputStyle} />
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label style={{ display: "grid", gap: 4 }}>
            <FieldLabel>Per session</FieldLabel>
            <input type="number" step="0.0001" value={perSession} onChange={(e) => setPerSession(e.target.value)} className="portal-input-inline" style={inputStyle} />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <FieldLabel>Per minute</FieldLabel>
            <input type="number" step="0.0001" value={perMinute} onChange={(e) => setPerMinute(e.target.value)} className="portal-input-inline" style={inputStyle} />
          </label>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label style={{ display: "grid", gap: 4 }}>
            <FieldLabel>Valid from</FieldLabel>
            <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="portal-input-inline" style={inputStyle} />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <FieldLabel>Valid till</FieldLabel>
            <input type="date" value={validTill} onChange={(e) => setValidTill(e.target.value)} className="portal-input-inline" style={inputStyle} />
          </label>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Small shared bits
// ---------------------------------------------------------------------------

function BackToCustomers() {
  return (
    <Link
      to="/admin/customers"
      aria-label="Back to customers"
      className="btn btn-secondary" style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}
    >
      <ArrowLeft size={14} /> Back to customers
    </Link>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: "0.9rem", fontWeight: 700 }}>{children}</span>;
}

function ErrorLine({ message }: { message: string }) {
  return (
    <div
      style={{
        color: "#c0392b",
        background: "#fdecea",
        border: "1px solid #e74c3c",
        borderRadius: 8,
        padding: 10,
        fontSize: "0.9rem",
      }}
    >
      {message}
    </div>
  );
}
